import React, { FC, memo, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';
import GlassPill from '../../Components/GlassPill';
import arrowBackIcon from '../../../../Assets/icons/arrow_back.svg';
import receiptIcon from '../../../../Assets/icons/receipt_long.svg';
import closeIcon from '../../../../Assets/icons/close.svg';
import StepFrame from './StepFrame';
import ChoiceButton from './ChoiceButton';

/**
 * The back pill sits in the card's top-left corner. StepFrame animates with a
 * transform, which makes it a stacking context painted over this slot - the
 * z-index keeps the pill on top so taps reach it.
 */
const BackSlot = styled.div`
  position: absolute;
  top: 32px;
  left: 32px;
  z-index: 1;
`;

const Relative = styled.div`
  position: relative;
`;

interface Props {
  /** True = print a receipt. Starts the payment either way. */
  onChoose: (wantsReceipt: boolean) => void;
  onBack: () => void;
}

/** "Receipt?" - two choices, and a way back to the menu. */
const ReceiptStep: FC<Props> = ({ onChoose, onBack }) => {
  const { i18n } = useI18n();
  /** A shopper hammering both buttons must not start two payments. */
  const chosenRef = useRef(false);

  const choose = useCallback(
    (wantsReceipt: boolean) => {
      if (chosenRef.current) {
        return;
      }
      chosenRef.current = true;
      onChoose(wantsReceipt);
    },
    [onChoose],
  );
  const handleYes = useCallback(() => choose(true), [choose]);
  const handleNo = useCallback(() => choose(false), [choose]);

  return (
    <Relative>
      <BackSlot>
        <GlassPill icon={arrowBackIcon} onClick={onBack} testId="epos-checkout-back">
          {i18n('Express.Receipt.GoBack')}
        </GlassPill>
      </BackSlot>
      <StepFrame
        testId="epos-receipt-step"
        title={i18n('Express.Receipt.Title')}
        subtitle={i18n('Express.Receipt.SubTitle')}
        actions={
          <>
            <ChoiceButton
              label={i18n('Express.Receipt.Yes')}
              icon={receiptIcon}
              onClick={handleYes}
              testId="epos-receipt-yes"
            />
            <ChoiceButton
              primary
              label={i18n('Express.Receipt.No')}
              icon={closeIcon}
              onClick={handleNo}
              testId="epos-receipt-no"
            />
          </>
        }
      />
    </Relative>
  );
};

export default memo(ReceiptStep);
